import { useCallback, useState } from 'react';

import { classificationTypes } from '@/components/Studies/Treatments/Treatment.utils';
import { errorToast } from '@/helpers';
import { _isNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import type { ClassificationType, Treatment } from '@/model/Treatment.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { useModalAction } from '@/utils/modal';

const listedClassifications: Record<ClassificationType, Array<ClassificationType>> = {
  treatment: ['treatment', 'pre_treatment'],
  pre_treatment: ['treatment', 'pre_treatment'],
  disease_induction: ['disease_induction'],
};

interface OverviewTreatmentInfoModal {
  loadingTreatment: boolean;
  openTreatmentInfo: (apiId: string, classification: ClassificationType) => Promise<void>;
}

/**
 * Overview rows only carry display strings. A label click loads the full treatment
 * and opens the same info modal as the Treatments column on the groups table.
 */
export const useOverviewTreatmentInfoModal = (study: Study | undefined): OverviewTreatmentInfoModal => {
  const [loadingTreatment, setLoadingTreatment] = useState<boolean>(false);
  const isMounted = useMountedState();
  const { openModal, closeModal } = useModalAction();

  const { invoke: fetchTreatments } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/treatments',
    invokeOnInit: false,
    options: { onError: { toast: false, capture: true, throw: true, slug: 'overview-treatment-load' } },
  });

  const openTreatmentInfo = useCallback(
    async (apiId: string, classification: ClassificationType): Promise<void> => {
      if (_isNil(study)) {
        errorToast(`Could not load ${classificationTypes[classification].key}`);
        return;
      }

      setLoadingTreatment(true);
      let treatment: Treatment | undefined;
      try {
        const response = await fetchTreatments({
          path: { studyApiId: study.api_id },
          query: {
            include: ['study_groups', 'metadata'],
            classification: listedClassifications[classification],
            perPage: -1,
          },
        });
        if (response.type === 'success') {
          treatment = (response.body.data as unknown as Array<Treatment>).find(
            ({ api_id: treatmentApiId }) => treatmentApiId === apiId
          );
        }
      } catch {
        treatment = undefined;
      } finally {
        if (isMounted()) {
          setLoadingTreatment(false);
        }
      }

      if (!isMounted()) {
        return;
      }

      if (_isNil(treatment)) {
        errorToast(`Could not load ${classificationTypes[classification].key}`);
        return;
      }

      openModal('TREATMENT_META', {
        treatment,
        closeModal,
      });
    },
    [closeModal, fetchTreatments, isMounted, openModal, study]
  );

  return { loadingTreatment, openTreatmentInfo };
};
